"use client";

import { MapPin, Phone, ArrowRight, Store } from "lucide-react";
import Image from "next/image";

export function StoreSelectionOverlay({ 
  stores, 
  onSelect, 
  brandName,
  brandImage
}: { 
  stores: any[]; 
  onSelect: (storeId: string) => void;
  brandName: string;
  brandImage?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col overflow-y-auto"
      style={{
        background: "linear-gradient(165deg, #f0f4ff 0%, #fafbff 35%, #f8f9fc 65%, #f0f2ff 100%)",
      }}
    >
      {/* Subtle decorative orbs */}
      <div
        className="pointer-events-none fixed top-[-15%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)" }}
      />
      <div
        className="pointer-events-none fixed bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, rgba(168,85,247,0.10) 0%, transparent 70%)" }}
      />

      {/* Header */}
      <div className="w-full px-4 pt-12 pb-6 sm:pt-16 sm:pb-8 text-center shrink-0 relative">
        {/* Brand profile */}
        <div className="relative inline-block mb-5 sm:mb-6">
          {brandImage ? (
            <div
              className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-2xl overflow-hidden shadow-lg"
              style={{
                border: "3px solid rgba(255,255,255,0.9)",
                boxShadow: "0 8px 30px rgba(99,102,241,0.15), 0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <Image
                src={brandImage}
                alt={brandName}
                width={96}
                height={96}
                className="object-cover w-full h-full"
                unoptimized
              />
            </div>
          ) : (
            <div
              className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-2xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                boxShadow: "0 8px 30px rgba(99,102,241,0.25)",
              }}
            >
              <Store className="w-9 h-9 sm:w-11 sm:h-11 text-white" />
            </div>
          )}
        </div>

        <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-2 sm:mb-3"
          style={{ color: "#1e1b4b" }}>
          Welcome to {brandName}
        </h1>
        <p className="text-sm sm:text-lg max-w-md mx-auto"
          style={{ color: "#6b7280" }}>
          Choose a branch to start shopping
        </p>
      </div>

      {/* Store Cards */}
      <div className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-8 pb-10 sm:pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-7">
          {stores.map((store) => {
            // Resolve effective images: own > inherited from main store > none
            const defaultStore = stores.find((s: any) => s.isDefault);
            const inheritFromMain = store.useMainStoreBranding === true && !store.isDefault;

            const effectiveCoverImages = (store.coverImages && store.coverImages.length > 0)
              ? store.coverImages
              : (inheritFromMain && defaultStore?.coverImages?.length > 0)
                ? defaultStore.coverImages
                : null;
            // The main shop's logo lives on the brand row (Store Settings
            // writes it there), so the main shop — and a branch set to "same
            // as main branch" — show the brand's logo, not a letter (Zeying,
            // Oct 2026).
            const mainProfileImage = brandImage || defaultStore?.profileImage || null;
            const effectiveProfileImage = store.isDefault || inheritFromMain
              ? mainProfileImage || store.profileImage || null
              : store.profileImage || null;

            const hasCover = !!effectiveCoverImages;
            const hasProfile = !!effectiveProfileImage;
            const hasAddress = store.address?.formattedAddress || store.address?.street;
            const hasPhone = !!store.phone;
            const addressText = store.address?.formattedAddress 
              || (store.address?.street ? `${store.address.street}, ${store.address.city || ''}` : null);

            return (
              <button
                key={store._id}
                onClick={() => onSelect(store._id)}
                className="group text-left rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                style={{
                  background: "#ffffff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(99,102,241,0.06)",
                  border: "1px solid rgba(226,232,240,0.8)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 8px 40px rgba(99,102,241,0.14), 0 2px 10px rgba(0,0,0,0.06)";
                  e.currentTarget.style.borderColor = "rgba(129,140,248,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(99,102,241,0.06)";
                  e.currentTarget.style.borderColor = "rgba(226,232,240,0.8)";
                }}
              >
                {/* Cover Photo Area */}
                <div className="relative w-full h-28 sm:h-36 overflow-hidden">
                  {hasCover ? (
                    <Image
                      src={effectiveCoverImages![0]}
                      alt={`${store.name} cover`}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      unoptimized
                    />
                  ) : (
                    <div
                      className="w-full h-full"
                      style={{ background: "#e5e7eb" }}
                    />
                  )}
                  {/* Gradient fade at bottom */}
                  <div className="absolute inset-x-0 bottom-0 h-12"
                    style={{ background: "linear-gradient(to top, rgba(255,255,255,0.9), transparent)" }} />


                </div>

                {/* Profile Image — overlapping the cover */}
                <div className="relative px-5 sm:px-6 -mt-8 sm:-mt-9">
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
                    style={{
                      border: "3px solid #ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      background: hasProfile ? "#ffffff" : "#f3f4f6",
                    }}
                  >
                    {hasProfile ? (
                      <Image
                        src={effectiveProfileImage!}
                        alt={store.name}
                        width={64}
                        height={64}
                        className="object-cover w-full h-full"
                        unoptimized
                      />
                    ) : (
                      <span className="text-xl sm:text-2xl font-black" style={{ color: "#6366f1" }}>
                        {store.name?.charAt(0)?.toUpperCase() || "S"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Body */}
                <div className="px-5 sm:px-6 pt-3 sm:pt-4 pb-5 sm:pb-6">
                  <h3 className="text-base sm:text-xl font-bold mb-1 line-clamp-1 transition-colors group-hover:text-indigo-600"
                    style={{ color: "#1e1b4b" }}>
                    {store.name}
                  </h3>

                  {/* Details */}
                  <div className="space-y-1.5 mt-2.5 min-h-[2.5rem]">
                    {addressText && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#a5b4fc" }} />
                        <span className="text-xs sm:text-sm leading-snug line-clamp-2" style={{ color: "#6b7280" }}>
                          {addressText}
                        </span>
                      </div>
                    )}
                    {hasPhone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 shrink-0" style={{ color: "#a5b4fc" }} />
                        <span className="text-xs sm:text-sm" style={{ color: "#6b7280" }}>
                          {store.phone}
                        </span>
                      </div>
                    )}
                    {!hasAddress && !hasPhone && (
                      <p className="text-xs sm:text-sm" style={{ color: "#9ca3af" }}>
                        Tap to browse this branch's collection
                      </p>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="mt-4 sm:mt-5 flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-semibold tracking-wide transition-colors group-hover:text-indigo-600"
                      style={{ color: "#4f46e5" }}>
                      See Products
                    </span>
                    <div
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-300 group-hover:translate-x-1"
                      style={{
                        background: "linear-gradient(135deg, #6366f1, #818cf8)",
                        boxShadow: "0 3px 10px rgba(99,102,241,0.3)",
                      }}
                    >
                      <ArrowRight className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer tagline */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-xs" style={{ color: "#c4b5fd" }}>
            Powered by Kuttos Technologies
          </p>
        </div>
      </div>
    </div>
  );
}
